import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  clearChapterComplete as clearLS,
  isChapterComplete as isLSComplete,
  markChapterComplete as markLSComplete,
  readProgress,
  type ChapterId,
} from "../../lib/chapterProgress";
import { isDemoChart } from "../../lib/entitlements";

/**
 * ChapterGateContext — exposes per-chart completion state for every chart-bound
 * chapter (1=Face, 2=Movement, 3=Journey, 4=Council, 5=Sound, 6=Sky, 7=Predictions)
 * to the watch tree (ChapterNav, TheFaceGate, MovementGate, future gates).
 *
 * Task 4: chapter ids are sourced from lib/journey.ts (JOURNEY_CHAPTERS) so the
 * context cannot drift from the bezel or the persistence layer. The 7 ids above
 * are the only ones that have a completion state — id 0 (Winding) has none.
 *
 * The chapter-agnostic methods (`isComplete(c)`, `markComplete(c)`,
 * `reset(c)`) are the canonical API; the FACE-specific accessors remain for
 * Step 1 callers that predate the generalization.
 *
 * Progress behavior: ChapterNav and ReadingPathway consult completion only to
 * distinguish read from unread. Entitlement locks are handled separately by
 * PaywallGate, so an unread chapter never masquerades as paid content.
 */

export interface ChapterGateCtx {
  chartId: string | null;
  /** Canonical: query completeness for any chapter. */
  isComplete: (chapter: ChapterId) => boolean;
  /** Canonical: mark a chapter acknowledged. */
  markComplete: (chapter: ChapterId) => void;
  /** Canonical: clear a chapter's acknowledgement (tests / reset). */
  reset: (chapter: ChapterId) => void;
  /** @deprecated — equivalent to isComplete(1). Kept for Step 1 callers. */
  isFaceComplete: boolean;
  /** @deprecated — equivalent to markComplete(1). Kept for Step 1 callers. */
  markFaceComplete: () => void;
  /** @deprecated — equivalent to reset(1). Kept for Step 1 callers. */
  resetFaceComplete: () => void;
}

const Ctx = createContext<ChapterGateCtx | null>(null);

const CHAPTERS_TO_TRACK: ChapterId[] = [1, 2, 3, 4, 5, 6, 7];
const EMPTY_COMPLETENESS: Record<ChapterId, boolean> = {
  1: false, 2: false, 3: false, 4: false, 5: false, 6: false, 7: false,
};

export function FaceGateProvider({
  chartId,
  children,
}: {
  chartId: string | null;
  children: ReactNode;
}) {
  // Single state object keyed by chapter — one source of truth.
  const [completeness, setCompleteness] = useState<Record<ChapterId, boolean>>({
    ...EMPTY_COMPLETENESS,
  });

  // Sync localStorage → in-memory state whenever chartId changes (and on mount).
  // Also supports a dev/test unlock: when the URL carries ?unlock=all,
  // every chapter is marked complete for the active chart. This lets the
  // user (and tests) jump directly to /sound or /sky without having to
  // acknowledge every prior chapter first. The flag is opt-in per page load.
  useEffect(() => {
    if (!chartId) {
      setCompleteness({ ...EMPTY_COMPLETENESS });
      return;
    }
    // Older builds incorrectly wrote every demo chapter as completed on first
    // open. Clear that fabricated state once per demo chart so returning
    // browsers receive the same honest progress model as fresh installs.
    if (isDemoChart(chartId)) {
      const migrationKey = `ag:demo-progress-v2:${chartId}`;
      try {
        if (localStorage.getItem(migrationKey) !== "1") {
          for (const c of CHAPTERS_TO_TRACK) clearLS(chartId, c);
          localStorage.setItem(migrationKey, "1");
        }
      } catch {
        /* Storage unavailable: in-memory defaults are already unread. */
      }
    }
    // ?unlock=all is an explicit dev/test override. Demo entitlement bypasses
    // paywalls, but it must not fabricate reading progress for a new visitor.
    const wantsUnlock =
      typeof window !== "undefined" &&
      new URLSearchParams(window.location.search).get("unlock") === "all";
    if (wantsUnlock) {
      for (const c of CHAPTERS_TO_TRACK) {
        markLSComplete(chartId, c);
      }
    }
    // Read the v2 record (which performs the v1→v2 migration lazily).
    const rec = readProgress(chartId, isDemoChart(chartId));
    const next: Record<ChapterId, boolean> = { ...EMPTY_COMPLETENESS };
    for (const c of CHAPTERS_TO_TRACK) {
      next[c] = isLSComplete(chartId, c) || (wantsUnlock ? true : false);
    }
    // LastChapter is tracked but the React state mirrors it through isComplete.
    // Read it eagerly so the consumer can read via getLastChapter() if needed.
    void rec.lastChapter;
    setCompleteness(next);
  }, [chartId]);

  const isComplete = useCallback(
    (chapter: ChapterId) => {
      return completeness[chapter] ?? false;
    },
    [completeness],
  );

  const markComplete = useCallback(
    (chapter: ChapterId) => {
      if (!chartId) return;
      markLSComplete(chartId, chapter);
      setCompleteness((prev) => ({ ...prev, [chapter]: true }));
    },
    [chartId],
  );

  const reset = useCallback(
    (chapter: ChapterId) => {
      if (!chartId) return;
      clearLS(chartId, chapter);
      setCompleteness((prev) => ({ ...prev, [chapter]: false }));
    },
    [chartId],
  );

  // Back-compat aliases for Step 1
  const isFaceComplete = isComplete(1);
  const markFaceComplete = useCallback(() => markComplete(1), [markComplete]);
  const resetFaceComplete = useCallback(() => reset(1), [reset]);

  const value = useMemo<ChapterGateCtx>(
    () => ({
      chartId,
      isComplete,
      markComplete,
      reset,
      isFaceComplete,
      markFaceComplete,
      resetFaceComplete,
    }),
    [chartId, isComplete, markComplete, reset, isFaceComplete, markFaceComplete, resetFaceComplete],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useFaceGate(): ChapterGateCtx {
  const ctx = useContext(Ctx);
  if (!ctx) {
    // Safe default so non-page contexts (e.g. global tooltips) don't crash.
    const noop = () => undefined;
    const noopFn = () => false;
    return {
      chartId: null,
      isComplete: noopFn,
      markComplete: noop,
      reset: noop,
      isFaceComplete: false,
      markFaceComplete: noop,
      resetFaceComplete: noop,
    };
  }
  return ctx;
}
